import type { DiscoveredBridge } from '@kinoq/camera-protocol';
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { type BridgeProbe, probeBridge } from './bridgeProbe';
import { loadCameraConfig, useCameraConfig } from './cameraConfig';
import { type DesktopApi, discoveryFor, shellApi, type TabletApi } from './desktopDiscovery';

export interface KnownBridge {
  id: string;
  name: string;
  host: string;
  port: number;
  manual: boolean;
  hidden: boolean;
  active: boolean;
  probe?: BridgeProbe;
}

const POLL_MS = 5000;
const NONE: DiscoveredBridge[] = [];

// Manual ids already handed to addBridge, per shell, so it runs once per page load.
const added = new WeakMap<DesktopApi | TabletApi, Set<string>>();

function addManual(api: DesktopApi | TabletApi, m: { id: string; host: string; port: number }) {
  // A failed add leaves the saved entry; its probe then reads "not found" and retries.
  api.bridges?.addBridge({ host: m.host, port: m.port }).catch(() => {});
}

function useDiscovered(api: DesktopApi | TabletApi | undefined): DiscoveredBridge[] {
  const discovery = api ? discoveryFor(api) : undefined;
  const subscribe = useCallback(
    (l: () => void) => discovery?.subscribe(l) ?? (() => {}),
    [discovery],
  );
  const bridges = useSyncExternalStore(subscribe, () => discovery?.getState().bridges ?? NONE);
  useEffect(() => {
    if (!discovery) return;
    discovery.start();
    return () => discovery.stop();
  }, [discovery]);
  return bridges;
}

/**
 * Every bridge this device knows: saved in the camera config, added by address, or
 * discovered now. Discovery losing a bridge never drops it; its probe tells its state.
 * Sorted by name. Empty outside a shell.
 */
export function useBridges({ poll }: { poll: boolean }): KnownBridge[] {
  const api = shellApi();
  const [config, update] = useCameraConfig();
  const discovered = useDiscovered(api);
  const [probes, setProbes] = useState<Record<string, BridgeProbe>>({});

  useEffect(() => {
    if (!api) return;
    const tried = added.get(api) ?? new Set<string>();
    added.set(api, tried);
    for (const m of config.manual) {
      if (tried.has(m.id)) continue;
      tried.add(m.id);
      // A hidden bridge is not handed to the shell; Show adds it then.
      if (!config.bridges[m.id]?.hidden) addManual(api, m);
    }
  }, [api, config.manual, config.bridges]);

  useEffect(() => {
    const changed = discovered.filter((b) => {
      const saved = config.bridges[b.id];
      return !saved || saved.name !== b.name || saved.host !== b.host || saved.port !== b.port;
    });
    if (changed.length === 0) return;
    update((c) => {
      const bridges = { ...c.bridges };
      for (const b of changed) {
        bridges[b.id] = {
          ...(bridges[b.id] ?? { active: false }),
          name: b.name,
          host: b.host,
          port: b.port,
        };
      }
      return { ...c, bridges };
    });
  }, [discovered, config.bridges, update]);

  const list = useMemo(() => {
    if (!api) return [];
    const live = new Map(discovered.map((b) => [b.id, b]));
    const manual = new Map(config.manual.map((m) => [m.id, m]));
    const ids = new Set([...Object.keys(config.bridges), ...manual.keys(), ...live.keys()]);
    return [...ids]
      .map((id): KnownBridge => {
        const saved = config.bridges[id];
        const where = live.get(id) ?? saved ?? manual.get(id);
        return {
          id,
          name: live.get(id)?.name ?? saved?.name ?? id,
          host: where?.host ?? '',
          port: where?.port ?? 0,
          manual: manual.has(id),
          hidden: saved?.hidden ?? false,
          active: saved?.active ?? false,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [api, config, discovered]);

  const probeKey = list
    .filter((b) => !b.hidden)
    .map((b) => b.id)
    .join(',');

  useEffect(() => {
    if (!api || probeKey === '') return;
    const controller = new AbortController();
    const run = () => {
      for (const id of probeKey.split(',')) {
        probeBridge(id, controller.signal).then((probe) => {
          if (controller.signal.aborted) return;
          setProbes((prev) => ({ ...prev, [id]: probe }));
          const m = probe.kind === 'unknown' && loadCameraConfig().manual.find((x) => x.id === id);
          if (m) addManual(api, m);
        });
      }
    };
    run();
    const timer = poll ? setInterval(run, POLL_MS) : undefined;
    return () => {
      clearInterval(timer);
      controller.abort();
    };
  }, [api, poll, probeKey]);

  return useMemo(() => list.map((b) => ({ ...b, probe: probes[b.id] })), [list, probes]);
}
